/*So in REG_registrationWindows 'billed' is used for ELO-P.
If true, then the registration fee is billed to the organization school district.
'waviedForStaff' is used to waive registration fees for STAR staff.
This is mostly always set to true. And the 'waivedForSubsidies' is used for PCOE etc. This is mostly always set to false.
The only fees that are ever paid during registration are the registration fees.*/

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Skeleton,
  Button,
  Typography,
  Alert,
  Card,
  List,
  Divider,
  Space,
  Tag,
  Tooltip,
  Row,
  Col,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import OnError from '../../../../components/OnError';
import {
  getSubsidyType,
  getSubsidyDisplayInfo,
  SubsidyType,
} from '../../../../utils/subsidy';

// Hooks
import { useFetchUser } from '../../../../hooks/useFetchUser';
import { useGetStudents } from '../../../../hooks/useGetStudents';
import { useGetSchedules } from '../../../../hooks/useGetSchedules';
import { useGetProgramListings } from '../../../../hooks/useGetProgramListings';
import { useGetRegistrationWindows } from '../../../../hooks/useGetRegistrationWindows';
import { useGetRegistrationWindowsBilledTo } from '../../../../hooks/useGetRegistrationWindowsBilledTo';
import { useGetOrganizationalAccounts } from '../../../../hooks/useGetOrganizationalAccounts';
import type { OrganizationAccountLink } from '../../../../hooks/useGetStudents';
import { useGetConcurrentEnrollmentDiscounts } from '../../../../hooks/useGetConcurrentEnrollmentDiscounts';
import { useGetScheduleBilledTo } from '../../../../hooks/useGetScheduleBilledTo';

// Local schedule hooks
import { useGet as useGetLocalSchedule } from '../../../../hooks/localSchedule/useGet';
import { formatCalendarDate } from '../../../../utils/formatDate';
import {
  dollars,
  formatTuitionTotals,
  sumTuition,
} from '../../../../utils/cartTotals';

import NextStepButton from '../../components/NextStepButton';
import CancelCheckoutButton from '../../components/CancelCheckoutButton';

const { Title, Paragraph, Text } = Typography;

/**
 * Basic currency formatting
 */
export { dollars };

/**
 * Simple helpers for summarizing selected days and checking if at least one day is selected.
 */
export const scheduleLib = () => {
  const summary = (selection: any) => {
    let scheduleString = '';
    if (selection.monday) scheduleString += 'M/';
    if (selection.tuesday) scheduleString += 'T/';
    if (selection.wednesday) scheduleString += 'W/';
    if (selection.thursday) scheduleString += 'TH/';
    if (selection.friday) scheduleString += 'F/';
    return scheduleString.slice(0, -1); // remove trailing slash
  };

  const valid = (selection: any) => {
    if (!selection) return null;
    const totalDaysSelected = [
      selection.monday,
      selection.tuesday,
      selection.wednesday,
      selection.thursday,
      selection.friday,
    ].filter(Boolean).length;
    return totalDaysSelected > 0 ? selection : null;
  };

  return [summary, valid];
};

export default function ConfirmSchedule({
  onNextStep,
  onCancel,
  isCanceling,
}: {
  onNextStep?: () => void;
  onCancel?: () => void;
  isCanceling?: boolean;
}) {
  const navigate = useNavigate();

  // Local schedule. Ignore the cart's 15-minute shopping timer here: it would
  // otherwise expire, and delete the cart, while the parent reads the
  // policies. The API re-checks availability when registering.
  const localSchedule = useGetLocalSchedule(true);

  // Load data via new hooks
  const {
    data: user,
    isLoading: loadingUser,
    error: userError,
  } = useFetchUser();
  const {
    data: students,
    isLoading: loadingStudents,
    error: studentsError,
  } = useGetStudents();
  const {
    data: schedulesData,
    isLoading: loadingSchedules,
    error: schedulesError,
  } = useGetSchedules();
  const {
    data: programListingsData,
    isLoading: loadingProgramListings,
    error: programListingsError,
  } = useGetProgramListings();
  const {
    data: regWindows,
    isLoading: loadingRegWindows,
    error: regWindowsError,
  } = useGetRegistrationWindows();
  const {
    data: regWindowsBilledToData,
    isLoading: loadingRegBilledTo,
    error: regWindowsBilledToError,
  } = useGetRegistrationWindowsBilledTo();
  const {
    data: orgAccounts,
    isLoading: loadingOrgAccounts,
    error: orgAccountsError,
  } = useGetOrganizationalAccounts();
  const {
    data: concurrencyDiscounts,
    isLoading: loadingDiscounts,
    error: concurrencyError,
  } = useGetConcurrentEnrollmentDiscounts();
  const {
    data: scheduleBilledToData,
    isLoading: loadingScheduleBilledTo,
    error: scheduleBilledToError,
  } = useGetScheduleBilledTo();

  // const removeLocalSchedule = useRemoveLocalSchedule();

  // Combined loading/error checks
  const isLoading =
    loadingUser ||
    loadingStudents ||
    loadingSchedules ||
    loadingProgramListings ||
    loadingRegWindows ||
    loadingRegBilledTo ||
    loadingOrgAccounts ||
    loadingDiscounts ||
    loadingScheduleBilledTo;

  if (isLoading) {
    return (
      <div data-testid="finalize-confirm-schedule-loading" role="progressbar">
        <Skeleton active />
      </div>
    );
  }

  // If any critical data is missing or there's an error
  if (
    userError ||
    studentsError ||
    schedulesError ||
    programListingsError ||
    regWindowsError ||
    regWindowsBilledToError ||
    orgAccountsError ||
    concurrencyError ||
    scheduleBilledToError ||
    !user ||
    !students ||
    !schedulesData ||
    !programListingsData ||
    !regWindows ||
    !regWindowsBilledToData ||
    !orgAccounts ||
    !concurrencyDiscounts ||
    !scheduleBilledToData
  ) {
    return (
      <OnError
        title="Missing or Failed to Load Required Data"
        subTitle="Please try again or contact support."
        extra={
          <Button type="dashed" onClick={() => navigate('/scheduling')}>
            Go Back
          </Button>
        }
        status={404}
      />
    );
  }

  // localSchedule might be undefined if not set yet
  if (!localSchedule || !localSchedule.selections) {
    return (
      <OnError
        title="No Schedule Found"
        subTitle="Please choose a schedule before continuing."
        extra={
          <Button type="dashed" onClick={() => navigate('/scheduling')}>
            Go Back
          </Button>
        }
      />
    );
  }

  // Access helper functions
  const [scheduleSummary, validSelection] = scheduleLib();

  /**
   * Identify unique registration windows for these selections.
   * We look up the window by: selection.program.registrationWindowId.
   */
  function getDistinctRegistrationWindowsForStudent(selections: any[]) {
    if (!regWindows) return [];
    const windows = selections
      .map((sel) => {
        const rwId = sel.program?.registrationWindowId;
        if (!rwId) return null;
        return regWindows.find((rw: any) => +rw.id === +rwId);
      })
      .filter((window, idx, self) => {
        return (
          window && self.findIndex((w: any) => +w.id === +window.id) === idx
        );
      });

    return windows;
  }

  /**
   * Display string for a registration fee, and the amount the parent pays
   * for it today (0 when waived, already paid or billed elsewhere).
   */
  function registrationFeeInfo(
    currentRegistration: any,
    paidRegistrationWindowIds: number[] = [],
    studentsSubsidyEligible: any,
    familyOrganizationAccounts: OrganizationAccountLink[] = []
  ): { text: string; amountDue: number } | null {
    if (!currentRegistration) return null;
    const notDue = (text: string) => ({ text, amountDue: 0 });

    // Waived for staff
    if (currentRegistration.waivedForStaff && user && (user as any).staff) {
      return notDue('Waived for Staff');
    }

    // Registration fees are per student: a sibling's payment for the same
    // window doesn't cover this student. The family-wide payment-status map
    // did, which showed $0 due for every student after the first checkout.
    if (
      paidRegistrationWindowIds.map(Number).includes(+currentRegistration.id)
    ) {
      return notDue('Already paid');
    }

    // Billed to org. Checkout (RegisterSchedule isBilledTo) bills the fee when
    // an eligible student's family is linked to one of the window's billed-to
    // organizations. A conditional row bills only students with an
    // organization account, which the eligibility check already ensures, so
    // conditional and unconditional rows both apply here. Name the family's
    // own organization, not just any organization on the window's list.
    if (studentsSubsidyEligible?.id) {
      // The API returns a flat list of billed-to rows, not a map by window id.
      const billedToReg = (regWindowsBilledToData || []).filter(
        (b: any) => +b.registrationWindowId === +currentRegistration.id
      );
      for (const b of billedToReg) {
        const orgAccount = familyOrganizationAccounts.find(
          (acc) => +b.userId === +acc.userId
        );
        if (orgAccount) {
          return notDue(`Billed to ${orgAccount.accountName}`);
        }
      }
    }

    // Billed separately
    if (currentRegistration.billed) {
      return notDue('Billed Separately');
    }

    // Default
    const amount = Number(currentRegistration.amount) || 0;
    return { text: dollars.format(amount), amountDue: amount };
  }

  /** Subsidized students see $0 tuition unless the fee is billed to an org. */
  function displayedTuition(student: any, sel: any): number {
    const subsidyInfo = getSubsidyDisplayInfo(
      getSubsidyType(student.studentsSubsidyEligible),
      student.studentsSubsidyEligible?.SubsidyProgram?.programName
    );
    return subsidyInfo.showActualPrice ? sel.fee || 0 : 0;
  }

  // Totals across every student, shown under the per-student cards.
  const studentsInCart = students.filter((student: any) =>
    localSchedule.selections.some((sel: any) => +sel.studentId === +student.id)
  );
  const registrationFeesDueToday = studentsInCart.reduce(
    (sum: number, student: any) =>
      sum +
      getDistinctRegistrationWindowsForStudent(
        localSchedule.selections.filter(
          (sel: any) => +sel.studentId === +student.id
        )
      ).reduce(
        (windowSum: number, regWindow: any) =>
          windowSum +
          (registrationFeeInfo(
            regWindow,
            student.paidRegistrationWindowIds,
            student.studentsSubsidyEligible,
            student.organizationAccounts
          )?.amountDue || 0),
        0
      ),
    0
  );
  const tuitionTotals = sumTuition(
    studentsInCart.flatMap((student: any) =>
      localSchedule.selections
        .filter(
          (sel: any) =>
            +sel.studentId === +student.id &&
            validSelection(sel) &&
            sel.schedule &&
            sel.program
        )
        .map((sel: any) => ({
          fee: displayedTuition(student, sel),
          monthly: sel.program.monthly,
        }))
    )
  );

  /**
   * Finalize enrollment
   */
  function enroll() {
    // If you have a new approach to collecting payment info or resetting timers, do it here.
    if (onNextStep) {
      onNextStep();
    } else {
      navigate('/finalize/payment');
    }
  }

  // --- Render a more polished UI ---
  return (
    <div data-testid="finalize-confirm-schedule">
      {/* Top Title */}
      <Title level={3} style={{ marginBottom: 16 }}>
        Review Your Selected Schedule
      </Title>

      {/* Info Alert */}
      <Alert
        type="info"
        message="Please review your selections carefully."
        description={
          <div>
            <Paragraph>
              By completing enrollment for these schedules, you agree to pay all
              associated tuition and fees when they are due.
            </Paragraph>
            {!user || !(user as any).elop?.id ? (
              <Paragraph strong style={{ marginBottom: 0 }}>
                Only registration fees will be due today.
              </Paragraph>
            ) : null}
          </div>
        }
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* ELO-P warning */}
      {orgAccounts.length > 0 && (
        <Alert
          data-testid="elo-p-warning"
          type="warning"
          message="ELO-P Funding"
          description={
            <Paragraph style={{ marginBottom: 0 }}>
              You may qualify for ELO-P funding. Some or all tuition could be
              covered by your district. Any covered fees will appear as $0 or
              &quot;Billed to &lt;District&gt;&quot;.
            </Paragraph>
          }
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Space
        data-testid="finalize-student-cards"
        direction="vertical"
        style={{ width: '100%' }}
        size="large"
      >
        {students.map((student: any, index: number) => {
          // Filter localSchedule.selections for this student
          const selectionsForThisStudent = localSchedule.selections.filter(
            (sel: any) => +sel.studentId === +student.id
          );
          if (!selectionsForThisStudent.length) return null;

          // Distinct windows
          const distinctWindows = getDistinctRegistrationWindowsForStudent(
            selectionsForThisStudent
          );

          return (
            <Card
              key={index}
              title={
                <Title level={4} style={{ margin: 0 }}>
                  {student.firstName}
                </Title>
              }
              bordered={true}
            >
              {/* Registration Fees List */}
              {!!distinctWindows.length && (
                <>
                  <Text strong>Registration Fees - DUE TODAY</Text>
                  <List
                    style={{ marginBottom: 16, marginTop: 8 }}
                    dataSource={distinctWindows}
                    renderItem={(regWindow: any) => {
                      const feeText = registrationFeeInfo(
                        regWindow,
                        student.paidRegistrationWindowIds,
                        student.studentsSubsidyEligible,
                        student.organizationAccounts
                      )?.text;
                      return (
                        <List.Item>
                          <List.Item.Meta
                            title={
                              <Text>{regWindow?.title} Registration Fee</Text>
                            }
                            description={feeText || 'N/A'}
                          />
                        </List.Item>
                      );
                    }}
                  />
                  <Divider style={{ margin: '12px 0' }} />
                </>
              )}

              {/* Tuition for each selection */}
              <Text strong>
                {(() => {
                  const hasNova = selectionsForThisStudent.some(
                    (sel) =>
                      sel.program?.title?.includes('Nova') ||
                      sel.program?.group?.includes('Nova')
                  );
                  const hasGalaxy = selectionsForThisStudent.some(
                    (sel) =>
                      sel.program?.title?.includes('Galaxy') ||
                      sel.program?.group?.includes('Galaxy')
                  );
                  const hasNonGalaxy = selectionsForThisStudent.some(
                    (sel) =>
                      sel.program &&
                      !sel.program.title?.includes('Galaxy') &&
                      !sel.program.group?.includes('Galaxy')
                  );

                  if (hasGalaxy && hasNonGalaxy) {
                    return 'Tuition';
                  } else if (hasGalaxy) {
                    return 'Tuition - DUE the 1st OF THE MONTH';
                  } else if (hasNova) {
                    return 'Tuition - DUE 1st DAY OF CLASS';
                  } else {
                    return 'Tuition - DUE 1st DAY OF CAMP';
                  }
                })()}
              </Text>
              <List
                style={{ marginTop: 8 }}
                dataSource={selectionsForThisStudent}
                renderItem={(sel: any, idx: number) => {
                  const validSel = validSelection(sel);
                  if (!validSel) return null;

                  const schedule = sel.schedule;
                  const program = sel.program;
                  if (!schedule || !program) return null;

                  // Use the same fee value stored in localSchedule.selections (same as SelectedSchedulesModal)
                  const fee = sel.fee || 0;

                  // Get subsidy information
                  const subsidyType = getSubsidyType(
                    student.studentsSubsidyEligible
                  );
                  const subsidyInfo = getSubsidyDisplayInfo(
                    subsidyType,
                    student.studentsSubsidyEligible?.SubsidyProgram?.programName
                  );

                  // Determine what price to display
                  const displayFee = subsidyInfo.showActualPrice ? fee : 0;
                  const scheduleTestId = `${student?.id ?? 'unknown'}-${
                    schedule?.id ?? idx
                  }`;

                  return (
                    <List.Item
                      key={idx}
                      data-testid={`finalize-selection-${scheduleTestId}`}
                    >
                      <List.Item.Meta
                        title={
                          <>
                            <Text strong>
                              {program.title}{' '}
                              {program.group ? `(${program.group})` : ''}
                            </Text>
                            {subsidyType !== SubsidyType.NONE && (
                              <>
                                {' '}
                                <Tag color={subsidyInfo.color}>
                                  {subsidyInfo.label}
                                </Tag>
                                {subsidyInfo.showActualPrice && (
                                  <Tooltip title="You see the fee amount, but the organization will be billed">
                                    <InfoCircleOutlined
                                      style={{
                                        color: '#1890ff',
                                        marginLeft: 4,
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </>
                            )}
                            <div style={{ fontSize: 13 }}>
                              {schedule.name} - Starts{' '}
                              {formatCalendarDate(program.startDate)}
                            </div>
                          </>
                        }
                        description={
                          <div style={{ marginTop: 4 }}>
                            <Text type="secondary">
                              Days Selected: {scheduleSummary(sel)}
                            </Text>
                            {subsidyType !== SubsidyType.NONE && (
                              <>
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {subsidyInfo.description}
                                </Text>
                              </>
                            )}
                          </div>
                        }
                      />
                      <div
                        data-testid={`finalize-selection-fee-${scheduleTestId}`}
                      >
                        {dollars.format(displayFee)}
                        {program.monthly ? ' / month' : ' total'}
                      </div>
                    </List.Item>
                  );
                }}
              />
            </Card>
          );
        })}
      </Space>

      <Card
        data-testid="finalize-totals"
        style={{ marginTop: 24, backgroundColor: '#fafafa' }}
      >
        <Row>
          <Col flex="auto">
            <Text strong>Registration fees due today</Text>
          </Col>
          <Col>
            <Text strong data-testid="finalize-total-due-today">
              {dollars.format(registrationFeesDueToday)}
            </Text>
          </Col>
        </Row>
        <Row style={{ marginTop: 8 }}>
          <Col flex="auto">
            <Text>Tuition</Text>
          </Col>
          <Col>
            <Text data-testid="finalize-total-tuition">
              {formatTuitionTotals(tuitionTotals)}
            </Text>
          </Col>
        </Row>
        <Text type="secondary" style={{ fontSize: 12 }}>
          The amount due today is confirmed on the payment step.
        </Text>
      </Card>

      <Divider />

      <div style={{ textAlign: 'center' }}>
        <div className="row">
          <div className="col-md-6 col-xs-6 col-sm-6">
            {onCancel && (
              <CancelCheckoutButton
                onCancel={onCancel}
                isCanceling={isCanceling}
              />
            )}
          </div>
          <div className="col-md-6 col-xs-6 col-sm-6">
            <NextStepButton disabled={false} onClick={enroll} />
          </div>
        </div>
      </div>
    </div>
  );
}
